import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { track } from '../../util/analytics';
import { MOBILE_SEARCH_PARAM } from '../../util/urlHelpers';
import { manageDisableScrolling } from '../../ducks/ui.duck';

import Modal from '../Modal/Modal';

import useSearchState from './useSearchState';
import SearchEditor, { MODE_BAR, MODE_DIALOG } from './SearchEditor';
import SearchPill from './SearchPill';

import css from './UnifiedSearch.module.css';

export const PLACEMENT_TOPBAR = 'topbar';
export const PLACEMENT_HERO = 'hero';

/** `?mobilesearch=` value per placement — each instance opens only for its own. */
export const DIALOG_PARAM_VALUES = { [PLACEMENT_TOPBAR]: 'open', [PLACEMENT_HERO]: 'hero' };

const DESKTOP_QUERY = '(min-width: 1024px)';

const matchesDesktop = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DESKTOP_QUERY).matches;

const searchWithDialogParam = (search, value) => {
  const params = new URLSearchParams(search || '');
  if (value) {
    params.set(MOBILE_SEARCH_PARAM, value);
  } else {
    params.delete(MOBILE_SEARCH_PARAM);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * The unified search — one element for every search entry point (spec §4).
 *
 * - `topbar`: the compact pill. A click opens the editor bar in place on desktop (decided at
 *   click time with matchMedia, never during render) and the full-screen dialog on phones.
 * - `hero`: on desktop the editor bar inline and always open, on phones the large pill that
 *   opens the dialog. Both variants are rendered and switched by CSS, so the server markup and
 *   the first client render are the same.
 *
 * The dialog state lives in the URL (`?mobilesearch=open|hero`, a UI-only param: no refetch, no
 * page_view), so Android Back closes it. Opening pushes one entry; closing goes back if this
 * session pushed it (else replaces it away); submitting replaces the dialog entry — one history
 * entry per search. A desktop submit pushes. Both navigate with `{ focusResultsHeading: true }`
 * as location state, for the results page to move focus to its heading.
 *
 * @component
 * @param {Object} props
 * @param {'topbar'|'hero'} [props.placement]
 * @param {'pill'|'icon'} [props.trigger] topbar only: `icon` = a 44px magnifier button instead of
 *   the pill (phone topbar on pages without results, where a full-width pill would only push the
 *   content down); it opens the same editor
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const UnifiedSearch = props => {
  const { placement = PLACEMENT_TOPBAR, trigger = 'pill', className } = props;
  const isHero = placement === PLACEMENT_HERO;
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { state, groups, location } = useSearchState();

  const [isMounted, setIsMounted] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [barOpen, setBarOpen] = useState(false);
  const [heroActive, setHeroActive] = useState(false);
  const pushedDialogRef = useRef(false);
  const pillRef = useRef(null);
  const heroActiveRef = useRef(false);

  const baseId = `UnifiedSearch-${placement}`;
  const pillId = `${baseId}-pill`;
  const dialogTitleId = `${baseId}-dialog-title`;
  const dialogValue = DIALOG_PARAM_VALUES[placement] || DIALOG_PARAM_VALUES[PLACEMENT_TOPBAR];

  useEffect(() => {
    setIsMounted(true);
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsDesktop(!!mql.matches);
    update();
    if (mql.addEventListener) {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    if (mql.addListener) {
      mql.addListener(update);
      return () => mql.removeListener(update);
    }
    return undefined;
  }, []);

  const { pathname, search, hash } = location;
  const dialogParamSet = new URLSearchParams(search || '').get(MOBILE_SEARCH_PARAM) === dialogValue;
  // Only after mount and only on phones: a reload with ?mobilesearch=open reopens the dialog
  // there, and never renders a portal into the server markup.
  const dialogOpen = isMounted && !isDesktop && dialogParamSet;

  useEffect(() => {
    if (!dialogParamSet) {
      pushedDialogRef.current = false;
    }
  }, [dialogParamSet]);

  // Any navigation closes the in-place bar.
  useEffect(() => {
    setBarOpen(false);
  }, [pathname, search]);

  const openDialog = () => {
    pushedDialogRef.current = true;
    history.push({ pathname, search: searchWithDialogParam(search, dialogValue), hash });
  };

  const closeDialog = () => {
    if (pushedDialogRef.current) {
      pushedDialogRef.current = false;
      history.goBack();
    } else {
      history.replace({ pathname, search: searchWithDialogParam(search, null), hash });
    }
  };

  const trackOpen = () => {
    track.ctaClick({ click_id: 'search_open', click_text: placement, click_url: pathname });
  };

  const handlePillClick = () => {
    trackOpen();
    if (!isHero && matchesDesktop()) {
      setBarOpen(true);
    } else {
      openDialog();
    }
  };

  const closeBar = useCallback(() => {
    setBarOpen(false);
    pillRef.current?.focus({ preventScroll: true });
  }, []);

  const activateHero = () => {
    if (!heroActiveRef.current) {
      heroActiveRef.current = true;
      setHeroActive(true);
      trackOpen();
    }
  };

  const submitInPlace = target => {
    setBarOpen(false);
    history.push(target, { focusResultsHeading: true });
  };

  const submitFromDialog = target => {
    pushedDialogRef.current = false;
    history.replace(target, { focusResultsHeading: true });
  };

  const onManageDisableScrolling = (componentId, disable) =>
    dispatch(manageDisableScrolling(componentId, disable));

  const pill =
    trigger === 'icon' && !isHero ? (
      <button
        ref={pillRef}
        id={pillId}
        type="button"
        className={css.iconTrigger}
        onClick={handlePillClick}
        aria-label={intl.formatMessage({ id: 'UnifiedSearch.pill.ariaLabelEmpty' })}
        aria-expanded={barOpen || dialogOpen}
        data-testid="unified-search-pill"
      >
        <Search size={20} strokeWidth={2} aria-hidden="true" />
      </button>
    ) : (
      <SearchPill
        ref={pillRef}
        id={pillId}
        state={state}
        variant={isHero ? 'large' : 'compact'}
        expanded={barOpen || dialogOpen}
        onClick={handlePillClick}
      />
    );

  const dialog = (
    <Modal
      id={`${baseId}-dialog`}
      isOpen={dialogOpen}
      onClose={closeDialog}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      focusElementId={pillId}
      containerClassName={css.dialogContainer}
      contentClassName={css.dialogContent}
      closeButtonMessage={intl.formatMessage({ id: 'UnifiedSearch.close' })}
    >
      {dialogOpen ? (
        <div
          className={css.dialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogTitleId}
          data-testid="unified-search-dialog"
        >
          <SearchEditor
            mode={MODE_DIALOG}
            idPrefix={`${baseId}-dialog`}
            titleId={dialogTitleId}
            state={state}
            groups={groups}
            onSubmit={submitFromDialog}
            onRequestClose={closeDialog}
            countsEnabled
          />
        </div>
      ) : null}
    </Modal>
  );

  if (isHero) {
    return (
      <div className={classNames(css.root, css.hero, className)}>
        <div className={css.heroDesktop}>
          <SearchEditor
            mode={MODE_BAR}
            idPrefix={`${baseId}-inline`}
            state={state}
            groups={groups}
            onSubmit={submitInPlace}
            countsEnabled={heroActive}
            onInteract={activateHero}
            resetKey={`${pathname}${search}`}
          />
        </div>
        <div className={css.heroPhone}>{pill}</div>
        {dialog}
      </div>
    );
  }

  return (
    <div className={classNames(css.root, css.topbar, className)}>
      {pill}
      {barOpen ? (
        <>
          <div className={css.backdrop} aria-hidden="true" />
          <div className={css.overlay}>
            <SearchEditor
              mode={MODE_BAR}
              idPrefix={`${baseId}-bar`}
              state={state}
              groups={groups}
              onSubmit={submitInPlace}
              onRequestClose={closeBar}
              closable
              autoFocusWhere
              countsEnabled
            />
          </div>
        </>
      ) : null}
      {dialog}
    </div>
  );
};

export default UnifiedSearch;
